// frontend/src/components/ROTeam/TeamOvertimePanel.js
import React, { useState, useEffect } from 'react';
import { Calendar, Clock, ShieldAlert, RefreshCw } from 'lucide-react';
import useMyTeamOvertimeData from '../../hooks/useMyTeamOvertimeData';
import OvertimeStatsCards from '../../pages/OvertimeMonitoring/components/OvertimeStatsCards';
import EmployeeOvertimeTable from '../../pages/OvertimeMonitoring/components/EmployeeOvertimeTable';
import EmployeeOvertimeDetailModal from '../../pages/OvertimeMonitoring/components/EmployeeOvertimeDetailModal';
import PillSelect from '../UI/PillSelect';
import ProcessingModal from '../UI/ProcessingModal';

// The team OT view reuses the full-company snapshot kernel (AR-sourced
// cap-eligible OT + cached live overlay + forecast loop), so the first
// paint / month switch can take several seconds — same profile as the
// admin dashboard. Mirror its delayed processing modal so a manager
// isn't left staring at a bare skeleton, while fast cached loads stay
// silent (the modal only mounts after MODAL_DELAY_MS).
const OT_LOADING_MESSAGES = [
  'Pulling cap-eligible overtime from attendance records...',
  'Normalising live clock events for in-progress days...',
  'Running 72-hour forecast for the month...',
  'Building your team roster...',
];
const OT_LOADING_EXPECTED_MS = 60000;
const MODAL_DELAY_MS = 400;

const formatMonthLabel = (yearMonth) => {
  if (!yearMonth) return '';
  try {
    const [y, m] = yearMonth.split('-');
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
  } catch (e) {
    return yearMonth;
  }
};

/**
 * TeamOvertimePanel — manager-scoped 72-hour overtime monitoring for the
 * "My Team" dashboard. Reuses the admin OvertimeMonitoring stat cards,
 * employee table, and detail modal verbatim, fed by the team-scoped
 * `useMyTeamOvertimeData` hook so a Reporting Officer / HOD sees only the
 * workmen under their care. Read-only: no alerts / reports / coverage.
 */
const TeamOvertimePanel = () => {
  const {
    stats,
    employees,
    forecastSummary,
    loading,
    isRefetching,
    forbidden,
    filters,
    selectedMonth,
    isCurrentMonth,
    monthOptions,
    cacheStatus,
    refetch,
    updateFilters,
    changeMonth,
    fetchEmployeesForMonth,
    getEmployeeHistory,
    getEmployeeDailyBreakdown,
  } = useMyTeamOvertimeData();

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Delayed processing-modal visibility — only surfaces for genuinely
  // slow loads (the skeleton covers fast cached ones).
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  useEffect(() => {
    if (!loading) {
      setShowLoadingModal(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowLoadingModal(true), MODAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  if (forbidden) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-sm">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldAlert className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-700 mb-2">No Team to Monitor</h2>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          Overtime monitoring is available to Reporting Officers, Trade Leaders,
          Managers, and Heads of Department. You don't currently have any team
          members under your care.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub-header + month selector */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Clock className="w-4 h-4 text-red-600" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
              72-Hour Overtime
            </h2>
            <p className="text-xs sm:text-sm text-gray-500">
              MOM compliance for your team — overtime counted toward the monthly 72-hour cap.
            </p>
          </div>
        </div>
        <div className="w-full sm:w-auto sm:flex-shrink-0 flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch?.({ force: true })}
            disabled={isRefetching || loading}
            title="Refresh now"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`} />
          </button>
          <PillSelect
            options={monthOptions}
            value={selectedMonth || ''}
            onChange={(val) => changeMonth?.(val)}
            theme="admin"
            icon={Calendar}
            iconColor="text-red-600"
            className="w-full sm:w-auto flex-1 sm:flex-initial min-w-0"
          />
        </div>
      </div>

      <OvertimeStatsCards
        stats={stats}
        loading={loading}
        forecastSummary={forecastSummary}
        isCurrentMonth={isCurrentMonth}
        onCardClick={(value) => updateFilters({ riskLevel: value })}
        activeFilter={filters.riskLevel}
      />

      <EmployeeOvertimeTable
        employees={employees}
        loading={loading}
        filters={filters}
        onFilterChange={updateFilters}
        onViewEmployee={(emp) => setSelectedEmployee(emp)}
        isCurrentMonth={isCurrentMonth}
        cacheStatus={cacheStatus}
        selectedMonth={selectedMonth}
        monthOptions={monthOptions}
        fetchEmployeesForMonth={fetchEmployeesForMonth}
      />

      <EmployeeOvertimeDetailModal
        isOpen={!!selectedEmployee}
        onClose={() => setSelectedEmployee(null)}
        employee={selectedEmployee}
        isCurrentMonth={isCurrentMonth}
        selectedMonth={selectedMonth}
        getEmployeeDailyBreakdown={getEmployeeDailyBreakdown}
        getEmployeeHistory={getEmployeeHistory}
      />

      <ProcessingModal
        isOpen={showLoadingModal}
        title="Loading Team Overtime"
        subtitle={`${formatMonthLabel(selectedMonth)} • Computing 72-hour cap status`}
        theme="admin"
        statusMessages={OT_LOADING_MESSAGES}
        rotateInterval={6000}
        estimatedDurationMs={OT_LOADING_EXPECTED_MS}
        icon={<Clock className="w-7 h-7" />}
      />
    </div>
  );
};

export default TeamOvertimePanel;
